import pino from 'pino';

// Google Cloud Run optimized Pino configuration
const createPinoLogger = () => {
  const baseConfig = {
    level: process.env.LOG_LEVEL || 'info',
    formatters: {
      level: (label: string) => {
        return { severity: label.toUpperCase() }; // Google Cloud severity mapping
      },
    },
    timestamp: () => `,"timestamp":"${new Date().toISOString()}"`,
  };

  // In production or when pretty printing is disabled, use structured JSON
  if (process.env.NODE_ENV === 'production' || process.env.DISABLE_PINO_PRETTY === 'true') {
    return pino(baseConfig);
  }

  // In development, try to use pretty printing but fallback gracefully
  try {
    return pino({
      ...baseConfig,
      transport: {
        target: 'pino-pretty',
        options: {
          colorize: true,
        },
      },
    });
  } catch (error: any) {
    console.warn('Pino pretty printing failed, falling back to structured JSON:', error?.message || String(error));
    return pino(baseConfig);
  }
};

const pinoLogger = createPinoLogger();

interface LogFlags {
  auth: boolean;
  api: boolean;
  database: boolean;
  performance: boolean;
  security: boolean;
  general: boolean;
}

export class Logger {
  private static enabled = process.env.LOG_ENABLED === 'true'; // Use env var, default to false
  private static usePino = process.env.USE_PINO_LOGGING === 'true';
  private static flags: LogFlags = {
    auth: process.env.LOG_AUTH === 'true',
    api: process.env.LOG_API === 'true',
    database: process.env.LOG_DATABASE === 'true',
    performance: process.env.LOG_PERFORMANCE === 'true',
    security: process.env.LOG_SECURITY === 'true',
    general: process.env.LOG_GENERAL === 'true',
  };
  private static allowedComponents: Set<string> = new Set(
    process.env.LOG_COMPONENTS ? process.env.LOG_COMPONENTS.split(',').map(c => c.trim()) : []
  );

  private static shouldLog(category?: keyof LogFlags, component?: string): boolean {
    if (!this.enabled) return false;
    if (category && this.flags[category]) return true;
    if (component && this.allowedComponents.has(component)) return true;
    return false;
  }

  static log(component: string, message: string, data?: unknown, category?: keyof LogFlags) {
    if (!this.shouldLog(category, component)) return;        
    if (this.usePino) {
      pinoLogger.info({ component, data, category }, message);
    } else {
      console.log(`[${component}] ${message}`, data || '');
    }
  }

  static error(component: string, message: string, error?: unknown, category?: keyof LogFlags) {
    //if (!this.shouldLog(category, component)) return;

    if (this.usePino) {
      const errorObj = error instanceof Error 
        ? { error: { message: error.message, stack: error.stack } }
        : { error };
      pinoLogger.error({ component, category, ...errorObj }, message);
    } else {
      if (error instanceof Error) {
        console.error(`[${component}] ${message}`);
        console.error(error.stack || error.message);
      } else if (typeof error === 'object' && error !== null) {
        console.error(`[${component}] ${message}`);
        console.error(JSON.stringify(error, null, 2));
      } else {
        console.error(`[${component}] ${message}`, error || '');
      }
    }
  }

  static warn(component: string, message: string, data?: unknown, category?: keyof LogFlags) {
    if (!this.shouldLog(category, component)) return;

    if (this.usePino) {
      pinoLogger.warn({ component, data, category }, message);
    } else {
      console.warn(`[${component}] ${message}`, data || '');
    }
  }

  // Top-level convenience info method to match existing usage across the codebase
  static info(component: string, message: string, data?: unknown, category?: keyof LogFlags) {
    // Delegate to the generic log implementation
    this.log(component, message, data, category);
  }

  static enable() {
    this.enabled = true;
  }

  static disable() {
    this.enabled = false;
  }

  static setEnabled(enabled: boolean) {
    this.enabled = enabled;
  }

  static enableCategory(category: keyof LogFlags) {
    this.flags[category] = true;
  }

  static disableCategory(category: keyof LogFlags) {
    this.flags[category] = false;
  }

  static allowComponent(component: string) {
    this.allowedComponents.add(component);
  }

  static disallowComponent(component: string) {
    this.allowedComponents.delete(component);
  }

  static enableAllCategories() {
    Object.keys(this.flags).forEach(key => {
      this.flags[key as keyof LogFlags] = true;
    });
  }

  static disableAllCategories() {
    Object.keys(this.flags).forEach(key => {
      this.flags[key as keyof LogFlags] = false;
    });
  }

  // New method specifically for API errors with additional context - Google Cloud optimized
  static apiError(component: string, message: string, error?: unknown, context?: {
    method?: string;
    url?: string;
    userId?: string;
    requestId?: string;
    action?: string;
    error?: string;
    orgId?: string;
    variantId?: string;
    warehouseId?: string;
  }) {
    if (!this.shouldLog('api', component)) return;

    if (this.usePino) {
      // Simplified context for Cloud Run searchability
      const logContext = {
        service: component,
        method: context?.method || 'UNKNOWN',
        action: context?.action || 'unknown',
        userId: context?.userId || 'anonymous',
        orgId: context?.orgId || 'unknown',
        // Remove verbose objects, keep only identifiers
        ...(context?.variantId && { variantId: context.variantId }),
        ...(context?.warehouseId && { warehouseId: context.warehouseId }),
        ...(context?.requestId && { requestId: context.requestId }),
        errorMessage: error instanceof Error ? error.message : String(error),
      };

      pinoLogger.error(logContext, message);
    } else {
      const timestamp = new Date().toISOString();
      const logPrefix = `[${component}][${timestamp}]`;
      console.error(`${logPrefix} ${message}`);
      if (context) {
        console.error(`${logPrefix} Context:`, {
          method: context.method,
          url: context.url,
          userId: context.userId,
          requestId: context.requestId,
          action: context.action,
          error: context.error,
        });
      }
      if (error instanceof Error) {
        console.error(`${logPrefix} Error:`, error.message);
        console.error(`${logPrefix} Stack:`, error.stack);
      } else if (typeof error === 'object' && error !== null) {
        console.error(`${logPrefix} Error Details:`, JSON.stringify(error, null, 2));
      } else if (error) {
        console.error(`${logPrefix} Error:`, error);
      }
    }
  }

  // Add new simplified methods for gradual adoption - Google Cloud optimized
  static logInfo(message: string, context: any = {}) {
    if (this.usePino) {
      pinoLogger.info(context, message);
    } else {
      this.log('General', message, context);
    }
  }

  static logError(message: string, error: any, context: any = {}) {
    if (this.usePino) {
      const logContext = {
        ...context,
        errorMessage: error?.message || String(error),
      };
      pinoLogger.error(logContext, message);
    } else {
      this.error('General', message, error);
    }
  }

  // Category-specific convenience methods
  static auth = {
    log: (component: string, message: string, data?: unknown) => 
      this.log(component, message, data, 'auth'),
    info: (component: string, message: string, data?: unknown) => 
      this.log(component, message, data, 'auth'),
    warn: (component: string, message: string, data?: unknown) => 
      this.warn(component, message, data, 'auth'),
    error: (component: string, message: string, error?: unknown) => 
      this.error(component, message, error, 'auth'),
  };

  static api = {
    log: (component: string, message: string, data?: unknown) => {
      if (!this.shouldLog('api', component)) return;
      
      if (this.usePino) {
        // Simplified context for info logs - Google Cloud optimized
        const logContext = typeof data === 'object' && data !== null ? {
          service: component,
          userId: (data as any).userId || 'anonymous',
          orgId: (data as any).orgId || 'unknown',
          // Include only essential identifiers
          ...((data as any).variantId && { variantId: (data as any).variantId }),
          ...((data as any).warehouseId && { warehouseId: (data as any).warehouseId }),
          ...((data as any).uniqueBarcodes && { uniqueBarcodes: (data as any).uniqueBarcodes }),
          ...((data as any).totalItems && { totalItems: (data as any).totalItems }),
        } : { service: component };

        pinoLogger.info(logContext, message);
      } else {
        this.log(component, message, data, 'api');
      }
    },
    info: (component: string, message: string, data?: unknown) => 
      this.api.log(component, message, data),
    warn: (component: string, message: string, data?: unknown) => 
      this.warn(component, message, data, 'api'),
    error: (component: string, message: string, error?: unknown) => 
      this.error(component, message, error, 'api'),
  };

  static security = {
    log: (component: string, message: string, data?: unknown) => 
      this.log(component, message, data, 'security'),
    info: (component: string, message: string, data?: unknown) => 
      this.log(component, message, data, 'security'),
    warn: (component: string, message: string, data?: unknown) => 
      this.warn(component, message, data, 'security'),
    error: (component: string, message: string, error?: unknown) => 
      this.error(component, message, error, 'security'),
  };

  static database = {
    log: (component: string, message: string, data?: unknown) => 
      this.log(component, message, data, 'database'),
    info: (component: string, message: string, data?: unknown) => 
      this.log(component, message, data, 'database'),
    warn: (component: string, message: string, data?: unknown) => 
      this.warn(component, message, data, 'database'),
    error: (component: string, message: string, error?: unknown) => 
      this.error(component, message, error, 'database'),
  };

  static performance = {
    log: (component: string, message: string, data?: unknown) => 
      this.log(component, message, data, 'performance'),
    info: (component: string, message: string, data?: unknown) => 
      this.log(component, message, data, 'performance'),
    warn: (component: string, message: string, data?: unknown) => 
      this.warn(component, message, data, 'performance'),
    error: (component: string, message: string, error?: unknown) => 
      this.error(component, message, error, 'performance'),
  };
}